/**
 * De btw-nummerchecker op /btw-nummer-controleren.
 *
 * Het formaat controleert de browser zelf, met dezelfde elfproef en mod-97-check
 * als app/Support/BtwNummer.php. Valt een nummer daar al af, dan gaat er geen
 * verzoek uit. Pas daarna vraagt de pagina het eindpunt /api/tools/btw-check om
 * de stand bij VIES, de dienst van de EU.
 */

const EINDPUNT = '/api/tools/btw-check';

/**
 * Het aantal tekens achter de landcode, per lidstaat. Dezelfde tabel als in
 * app/Support/BtwNummer.php.
 */
const LENGTES = {
    AT: [9], BE: [10], BG: [9, 10], CY: [9], CZ: [8, 9, 10], DE: [9], DK: [8],
    EE: [9], EL: [9], ES: [9], FI: [8], FR: [11], HR: [11], HU: [8], IE: [8, 9],
    IT: [11], LT: [9, 12], LU: [8], LV: [11], MT: [8], NL: [12], PL: [10],
    PT: [9], RO: [2, 3, 4, 5, 6, 7, 8, 9, 10], SE: [12], SI: [8], SK: [10],
};

const datumtijd = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const formulier = document.querySelector('[data-btwcheck]');

if (formulier) {
    start(formulier);
}

function start(formulier) {
    const veld = formulier.querySelector('[data-veld="vat"]');
    const knop = formulier.querySelector('[data-actie="controleer"]');
    const uitslag = document.querySelector('[data-btwcheck-uitslag]');

    formulier.addEventListener('submit', (event) => {
        event.preventDefault();
        controleer();
    });

    async function controleer() {
        const vat = normaliseer(veld.value);

        if (vat === '') {
            toon(uitslag, 'onbekend', 'Vul een btw-nummer in, bijvoorbeeld NL123456789B01.');

            return;
        }

        if (! formaatGeldig(vat)) {
            toon(uitslag, 'ongeldig', 'Dit is geen geldig btw-nummerformaat. Een Nederlands nummer bestaat uit NL, negen cijfers, een B en twee cijfers.');

            return;
        }

        veld.value = vat;
        knop.disabled = true;
        toon(uitslag, 'bezig', 'Bezig met de controle bij VIES…');

        try {
            const antwoord = await fetch(EINDPUNT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ vat }),
            });

            toonAntwoord(uitslag, vat, antwoord.status, await antwoord.json());
        } catch (fout) {
            toon(uitslag, 'storing', 'De controle kon niet worden verstuurd. Kijk je verbinding na en probeer het opnieuw.');
        } finally {
            knop.disabled = false;
        }
    }
}

function toonAntwoord(uitslag, vat, httpStatus, data) {
    if (httpStatus === 429) {
        toon(uitslag, 'storing', data?.message ?? 'Je hebt veel nummers achter elkaar gecontroleerd. Wacht een minuut en probeer het opnieuw.');

        return;
    }

    if (data?.status === 'invalid_format') {
        toon(uitslag, 'ongeldig', data.message);

        return;
    }

    /*
     * Een storing bij VIES of bij een lidstaat is geen ongeldig nummer. Wie een
     * factuur met 0% btw stuurt op grond van een "ongeldig" dat eigenlijk een
     * storing was, zit met een naheffing, dus die twee blijven uit elkaar.
     */
    if (httpStatus !== 200 || data?.status === 'unavailable') {
        toon(uitslag, 'storing', 'De controle-service van de EU is tijdelijk niet beschikbaar. Probeer het over een paar minuten opnieuw.');

        return;
    }

    if (data.status === 'valid') {
        toon(uitslag, 'geldig', 'Dit btw-nummer is geldig.', regelsBijGeldig(vat, data));

        return;
    }

    toon(uitslag, 'ongeldig', 'Dit btw-nummer is niet geregistreerd bij de EU (VIES).', [
        ['Gecontroleerd nummer', vat],
    ]);
}

/**
 * @returns {Array<[string, string]>}
 */
function regelsBijGeldig(vat, data) {
    const regels = [['Btw-nummer', vat]];

    if (data.name) {
        regels.push(['Op naam van', data.name]);
    }

    if (data.address) {
        regels.push(['Adres', data.address]);
    }

    if (data.requestIdentifier) {
        regels.push(['Raadplegingsnummer', data.requestIdentifier]);
    }

    const datum = data.requestDate ? new Date(data.requestDate) : null;

    if (datum && ! Number.isNaN(datum.valueOf())) {
        regels.push(['Gecontroleerd op', datumtijd.format(datum)]);
    }

    return regels;
}

/**
 * @param {Array<[string, string]>} regels
 */
function toon(uitslag, soort, bericht, regels = []) {
    uitslag.hidden = false;
    uitslag.className = 'btw-uitslag btw-uitslag--' + soort;
    uitslag.replaceChildren();

    const kop = document.createElement('p');
    kop.className = 'btw-uitslag__bericht';
    kop.textContent = bericht;
    uitslag.append(kop);

    if (regels.length === 0) {
        return;
    }

    const lijst = document.createElement('dl');
    lijst.className = 'btw-uitslag__gegevens';

    for (const [naam, waarde] of regels) {
        const dt = document.createElement('dt');
        dt.textContent = naam;

        const dd = document.createElement('dd');
        dd.textContent = waarde;

        lijst.append(dt, dd);
    }

    uitslag.append(lijst);
}

function normaliseer(waarde) {
    return waarde.replace(/[\s.\-]/g, '').toUpperCase();
}

function formaatGeldig(vat) {
    const land = vat.slice(0, 2);
    const rest = vat.slice(2);

    if (! Object.hasOwn(LENGTES, land) || ! /^[A-Z0-9+*]+$/.test(rest)) {
        return false;
    }

    if (! LENGTES[land].includes(rest.length)) {
        return false;
    }

    if (land !== 'NL') {
        return true;
    }

    if (! /^NL[0-9]{9}B[0-9]{2}$/.test(vat)) {
        return false;
    }

    return elfproef(vat.slice(2, 11)) || mod97(vat);
}

/** Het oude omzetbelastingnummer van rechtspersonen en vof's. */
function elfproef(cijfers) {
    let som = 0;

    for (let i = 0; i < 8; i++) {
        som += Number(cijfers[i]) * (9 - i);
    }

    const rest = som % 11;

    return rest < 10 && rest === Number(cijfers[8]);
}

/** Het btw-identificatienummer van eenmanszaken, sinds 2020. Mod 97, zoals een IBAN. */
function mod97(vat) {
    let getal = '';

    for (const teken of vat) {
        getal += /[A-Z]/.test(teken) ? String(teken.charCodeAt(0) - 55) : teken;
    }

    let rest = 0;

    for (let i = 0; i < getal.length; i += 7) {
        rest = Number(String(rest) + getal.slice(i, i + 7)) % 97;
    }

    return rest === 1;
}
